import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Square, ExternalLink, Volume2, Settings } from 'lucide-react';
import MediaPlayerService from '../services/MediaPlayerService';

const PersistentMediaPlayer = () => {
  // Use window.location for path detection to work outside Router context
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [currentSearch, setCurrentSearch] = useState(window.location.search);
  
  // Extract sheetId from the URL path
  const pathMatch = currentPath.match(/\/sheet\/([^/?]+)/);
  const sheetId = pathMatch ? pathMatch[1] : null;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentSheet, setCurrentSheet] = useState(null);
  const progressRef = useRef(null);
  const isDragging = useRef(false);
  
  // Check if we're on a sheet page
  const isSheetPage = currentPath.includes('/sheet/');
  
  // Check if we're in perform mode
  const urlParams = new URLSearchParams(currentSearch);
  const isPerformMode = urlParams.get('perform') === 'true';
  
  // Listen for URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      setCurrentSearch(window.location.search);
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);
    
    // Also listen for custom navigation events if needed
    window.addEventListener('pushstate', handleLocationChange);
    window.addEventListener('replacestate', handleLocationChange);

    // Override pushState and replaceState to dispatch events
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function() {
      originalPushState.apply(window.history, arguments);
      window.dispatchEvent(new Event('pushstate'));
    };
    
    window.history.replaceState = function() {
      originalReplaceState.apply(window.history, arguments);
      window.dispatchEvent(new Event('replacestate'));
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('pushstate', handleLocationChange);
      window.removeEventListener('replacestate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);
  
  // Debug logging
  console.log('PersistentMediaPlayer Debug:', {
    sheetId,
    isSheetPage,
    pathname: currentPath,
    mediaUrl,
    currentSheet,
    isPerformMode
  });
  
  // Subscribe to MediaPlayerService updates
  useEffect(() => {
    if (!sheetId) return;
    
    const unsubscribe = MediaPlayerService.subscribe(sheetId, (state) => {
      if (state === 'playing') {
        setIsPlaying(true);
      } else if (state === 'paused' || state === 'stopped') {
        setIsPlaying(false);
      }
    });
    
    return () => unsubscribe();
  }, [sheetId]);
  
  // Fetch sheet data when sheetId changes
  useEffect(() => {
    if (!sheetId || !isSheetPage) {
      setMediaUrl('');
      setCurrentSheet(null);
      return;
    }
    
    const fetchSheetData = async () => {
      try {
        console.log('Fetching sheet data for:', sheetId);
        const response = await fetch(`/api/sheets/${sheetId}`);
        if (response.ok) {
          const data = await response.json();
          console.log('Sheet data received:', data);
          if (data.success && data.data) {
            const allProperties = Object.keys(data.data);
            console.log('All sheet properties:', allProperties.join(', '));
            console.log('Looking for media fields...');
            allProperties.filter(prop => prop.toLowerCase().includes('media') || prop.toLowerCase().includes('youtube') || prop.toLowerCase().includes('link')).forEach(prop => {
              console.log(`  ${prop}:`, data.data[prop]);
            });
            setMediaUrl(data.data.mediaUrl || data.data.media_link || '');
            setCurrentSheet(data.data);
            console.log('Media URL set to:', data.data.mediaUrl || data.data.media_link);
          }
        } else {
          console.error('Failed to fetch sheet:', response.status);
        }
      } catch (error) {
        console.error('Error fetching sheet data:', error);
      }
    };
    
    fetchSheetData();
  }, [sheetId, isSheetPage]);
  
  // Update time periodically when playing
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      // Check for YouTube player
      if (MediaPlayerService.youtubePlayer && MediaPlayerService.youtubePlayer.getCurrentTime) {
        try {
          const time = MediaPlayerService.youtubePlayer.getCurrentTime();
          const dur = MediaPlayerService.youtubePlayer.getDuration();
          setCurrentTime(time);
          setDuration(dur);
        } catch (error) {
          // Player might not be ready yet
          console.debug('Error getting time from YouTube player:', error);
        }
      }
      // Check for HTML5 audio player
      else if (MediaPlayerService.currentPlayer && MediaPlayerService.currentPlayer.currentTime !== undefined) {
        setCurrentTime(MediaPlayerService.currentPlayer.currentTime);
        setDuration(MediaPlayerService.currentPlayer.duration || 0);
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  const handlePlayPause = async () => {
    if (!mediaUrl) return;
    
    setIsLoading(true);
    try {
      if (isPlaying) {
        await MediaPlayerService.pause();
      } else {
        await MediaPlayerService.play(sheetId, mediaUrl);
      }
    } catch (error) {
      console.error('Error controlling playback:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStop = () => {
    MediaPlayerService.stop();
    setCurrentTime(0);
  };
  
  const handleSeek = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    
    // Check for YouTube player
    if (MediaPlayerService.youtubePlayer && MediaPlayerService.youtubePlayer.seekTo) {
      MediaPlayerService.youtubePlayer.seekTo(newTime, true);
    }
    // Check for HTML5 audio player
    else if (MediaPlayerService.currentPlayer && MediaPlayerService.currentPlayer.currentTime !== undefined) {
      MediaPlayerService.currentPlayer.currentTime = newTime;
    }
    
    setCurrentTime(newTime);
  };
  
  const handleMouseDown = (e) => {
    isDragging.current = true;
    handleSeek(e);
  };
  
  const handleMouseMove = (e) => {
    if (isDragging.current) {
      handleSeek(e);
    }
  };
  
  const handleMouseUp = () => {
    isDragging.current = false;
  };
  
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleOpenLink = () => {
    if (mediaUrl) {
      window.open(mediaUrl, '_blank');
    }
  };
  
  // Toggle the PrintViewControls visibility
  const handleToggleControls = () => {
    // Dispatch a custom event to toggle the PrintViewControls
    window.dispatchEvent(new CustomEvent('togglePrintControls'));
  };
  
  // Don't show if not on a sheet page or no media URL
  if (!isSheetPage || !mediaUrl) return null;
  
  return (
    <div 
      className="bg-white border-t border-gray-200 shadow-lg no-print"
      style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        width: '100vw',
        boxSizing: 'border-box'
      }}
    >
        <div className="w-full px-2 sm:px-4 py-2 flex items-center justify-between gap-2">
          {/* Media Player Controls Group */}
          <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={isLoading || !mediaUrl}
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isLoading ? (
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <Play className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
            
            {/* Stop Button - Hidden on mobile */}
            <button
              onClick={handleStop}
              className="hidden sm:block p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              title="Stop"
            >
              <Square className="w-4 h-4" />
            </button>
            
            {/* Time Display - Smaller on mobile */}
            <div className="text-xs sm:text-sm text-gray-600 min-w-[60px] sm:min-w-[80px] flex-shrink-0">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
            
            {/* Progress Bar - Takes remaining space */}
            <div className="flex-1 relative min-w-[60px]">
              <div
                ref={progressRef}
                className="h-1.5 sm:h-2 bg-gray-200 rounded-full cursor-pointer relative overflow-hidden"
                onMouseDown={handleMouseDown}
              >
                <div
                  className="absolute left-0 top-0 h-full bg-blue-600 rounded-full transition-none"
                  style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                />
                {duration > 0 && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2 sm:w-3 h-2 sm:h-3 bg-blue-600 rounded-full shadow-md transition-none"
                    style={{ left: `${(currentTime / duration) * 100}%`, marginLeft: '-4px' }}
                  />
                )}
              </div>
            </div>
            
            {/* Song Info - Hidden on mobile */}
            {currentSheet && (
              <div className="hidden lg:flex items-center gap-2 text-sm flex-shrink-0">
                <Volume2 className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700 font-medium">
                  {currentSheet.title || 'Untitled'}
                </span>
                {currentSheet.artist && (
                  <span className="text-gray-500">- {currentSheet.artist}</span>
                )}
              </div>
            )}
            
            {/* Open Link Button - Smaller on mobile */}
            <button
              onClick={handleOpenLink}
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              title="Open in new tab"
            >
              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
          
          {/* Settings Button - Separated to far right */}
          {isPerformMode && (
            <button
              onClick={handleToggleControls}
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              title="Performance settings"
            >
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          )}
        </div>
    </div>
  );
};

export default PersistentMediaPlayer;